import { SagaIterator } from 'redux-saga';
import { all, call, put, select } from 'redux-saga/effects';
import { PerformanceMetric } from '../../../constants/telemetry';
import { GetRemoteConnectionResponse } from '../../../data/services/data-plane-api/dev-box';
import { ClientError, FailureOperation, isAggregatedFailure, isAggregatedSuccess } from '../../../models/common';
import { combineResults, getErrorCodes } from '../../../utilities/aggregated-result';
import { aggregateFailureResponses } from '../../../utilities/failure';
import { trackTimedPerformanceMetric } from '../../../utilities/telemetry/channel';
import {
    getRemoteConnection,
    loadRemoteConnectionsForDevBoxes,
    loadRemoteConnectionsForDevBoxesError,
    loadRemoteConnectionsForDevBoxesFailed,
    loadRemoteConnectionsForDevBoxesSuccess,
} from '../../actions/remote-connection/remote-connection-action-creators';
import { LoadRemoteConnectionsForDevBoxesAction } from '../../actions/remote-connection/remote-connection-actions';
import { createSagaError } from '../../effects/create-saga-error';
import { putAndAwait } from '../../effects/put-and-await';
import { rejectAction } from '../../effects/reject-action';
import { resolveAction } from '../../effects/resolve-action';
import { takeLeading } from '../../effects/take';
import { AsyncOutcome } from '../../store/common-state';
import { getIdsForDevBoxesRequiringRemoteConnections } from './selectors';

export function* loadRemoteConnectionsForDevBoxesSaga(action: LoadRemoteConnectionsForDevBoxesAction): SagaIterator {
    const startTime = new Date();

    try {
        const ids: string[] = yield select(getIdsForDevBoxesRequiringRemoteConnections);

        const responses: GetRemoteConnectionResponse[] = yield all(
            ids.map((id) => putAndAwait(getRemoteConnection({ id })))
        );

        const completeResult = combineResults(responses);

        if (isAggregatedFailure(completeResult)) {
            // All requests failed
            const { failures } = completeResult;
            const failure = aggregateFailureResponses(FailureOperation.LoadRemoteConnectionsForDevBoxes, ...failures);
            yield put(loadRemoteConnectionsForDevBoxesFailed({ failure }));
        } else {
            yield put(loadRemoteConnectionsForDevBoxesSuccess());
        }

        yield resolveAction(action, completeResult);

        yield call(
            trackTimedPerformanceMetric,
            PerformanceMetric.LoadRemoteConnectionsForDevBoxes,
            startTime,
            completeResult.outcome,
            isAggregatedSuccess(completeResult) ? undefined : { errorCodes: getErrorCodes(completeResult) }
        );
    } catch (err) {
        const error: ClientError = yield createSagaError(err, FailureOperation.LoadRemoteConnectionsForDevBoxes);
        yield put(loadRemoteConnectionsForDevBoxesError({ error }));
        yield rejectAction(action, error);

        yield call(
            trackTimedPerformanceMetric,
            PerformanceMetric.LoadRemoteConnectionsForDevBoxes,
            startTime,
            AsyncOutcome.Error,
            { errorCodes: [error.code] }
        );
    }
}

export function* loadRemoteConnectionsForDevBoxesListenerSaga(): SagaIterator {
    yield takeLeading(loadRemoteConnectionsForDevBoxes, loadRemoteConnectionsForDevBoxesSaga);
}
